/**
 * useUserPreferences Hook
 * Custom hook for fetching and updating user preferences using TanStack Query.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { userApi } from '../api/transactionApi';
import { useUIStore } from '../store/uiStore';
import { useBudgetStore } from '../store/budgetStore';
import { languageMap, type SupportedLanguage } from '../i18n';
import type { Theme } from '../types';
import { useEffect } from 'react';

export const userPreferencesKeys = {
  all: ['userPreferences'] as const,
  preferences: () => [...userPreferencesKeys.all, 'current'] as const,
};

/**
 * Hook for fetching user preferences and syncing with stores
 */
export function useUserPreferences() {
  const { i18n } = useTranslation();
  const { setTheme } = useUIStore();
  const { setTotalBudget, syncFromServer } = useBudgetStore();

  const query = useQuery({
    queryKey: userPreferencesKeys.preferences(),
    queryFn: userApi.getPreferences,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  // Sync preferences with stores when data changes
  useEffect(() => {
    if (query.data?.success && query.data.data) {
      const { theme, language, budgetConfig } = query.data.data;

      // Sync theme
      setTheme(theme.toLowerCase() as 'dark' | 'light');

      // Sync language
      if (language) {
        const i18nLang = languageMap[language] as SupportedLanguage;
        if (i18nLang && i18n.language !== i18nLang) {
          i18n.changeLanguage(i18nLang);
          localStorage.setItem('ff_language', i18nLang);
        }
      }

      // Sync budget
      if (budgetConfig) {
        setTotalBudget(budgetConfig.totalBudget);
        if (budgetConfig.categories && budgetConfig.categories.length > 0) {
          syncFromServer(budgetConfig.categories);
        }
      }
    }
  }, [query.data, setTheme, setTotalBudget, syncFromServer, i18n]);

  return {
    preferences: query.data?.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for updating user preferences
 */
export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: userApi.updatePreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userPreferencesKeys.all });
      addToast('success', 'Preferences updated');
    },
    onError: (error: Error) => {
      addToast('error', error.message);
    },
  });
}

/**
 * Hook for theme management with API sync
 */
export function useTheme() {
  const { theme, setTheme } = useUIStore();
  const updatePreferences = useUpdatePreferences();

  const toggleTheme = () => {
    const newTheme: Theme = theme === 'dark' ? 'LIGHT' : 'DARK';

    // Update local state immediately
    setTheme(newTheme.toLowerCase() as 'dark' | 'light');

    // Sync with server
    updatePreferences.mutate({ theme: newTheme });
  };

  const setThemeWithSync = (newTheme: Theme) => {
    setTheme(newTheme.toLowerCase() as 'dark' | 'light');
    updatePreferences.mutate({ theme: newTheme });
  };

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') {
      root.classList.remove('dark');
      root.classList.add('light');
    } else {
      root.classList.remove('light');
      root.classList.add('dark');
    }
  }, [theme]);

  return {
    theme,
    isDark: theme === 'dark',
    toggleTheme,
    setTheme: setThemeWithSync,
    isUpdating: updatePreferences.isPending,
  };
}
